import React, { useState, useEffect } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import { supabase } from '../../supabaseClient';
import './PdfViewer.css';

// Set up the react-pdf worker via unpkg CDN
pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

// `filePath` (unauthenticated) is for free preview PDFs. `itemType`+`itemId`
// with `secure` set fetches the real, purchase-gated PDF: the server looks up
// the actual storage path itself after verifying entitlement, so the raw path
// is never exposed to — or trusted from — the client.
const PdfViewer = ({ filePath, itemType, itemId, secure = false, pageNumber = 1, zoomLevel = 1, onLoadSuccess }) => {
  const [pdfUrl, setPdfUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pageWidth, setPageWidth] = useState(null);

  useEffect(() => {
    const container = document.querySelector('.pdf-viewer-container');
    if (!container) return;

    const observer = new ResizeObserver((entries) => {
      window.requestAnimationFrame(() => {
        for (let entry of entries) {
          if (entry.contentRect.width > 0) {
            setPageWidth(entry.contentRect.width - 32);
          }
        }
      });
    });

    observer.observe(container);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (secure ? !itemType || !itemId : !filePath) return;

    const fetchSignedUrl = async () => {
      setLoading(true);
      setError(null);
      try {
        let url;
        const fetchOptions = {};

        if (secure) {
          const { data: { session } } = await supabase.auth.getSession();
          if (!session) {
            throw new Error('Please log in to view this document');
          }
          url = `/api/get-signed-url?itemType=${encodeURIComponent(itemType)}&itemId=${encodeURIComponent(itemId)}&variant=full`;
          fetchOptions.headers = { Authorization: `Bearer ${session.access_token}` };
        } else {
          url = `/api/get-signed-url?filePath=${encodeURIComponent(filePath)}`;
        }

        const response = await fetch(url, fetchOptions);
        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.error || 'Failed to fetch PDF URL');
        }

        setPdfUrl(data.signedUrl);
      } catch (err) {
        console.error("Error fetching PDF:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchSignedUrl();
  }, [filePath, itemType, itemId, secure]);

  // Prevent right-click to add basic anti-piracy
  const handleContextMenu = (e) => {
    e.preventDefault();
  };

  if (loading) {
    return (
      <div className="pdf-viewer-container loading">
        <div className="loader"></div>
        <p>Loading Document...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="pdf-viewer-container error">
        <p>Error loading document: {error}</p>
      </div>
    );
  }

  return (
    <div className="pdf-viewer-container" onContextMenu={handleContextMenu}>
      {pdfUrl && (
        <Document
          file={pdfUrl}
          onLoadSuccess={onLoadSuccess}
          loading={
            <div className="pdf-viewer-container loading" style={{ height: '100%', width: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
               <div className="loader"></div>
               <p>Loading Document...</p>
            </div>
          }
          error={<p style={{ padding: '2rem', color: 'var(--rose)' }}>Failed to render PDF.</p>}
        >
          <Page 
            pageNumber={pageNumber} 
            scale={zoomLevel}
            width={pageWidth ? Math.min(pageWidth, 800) : undefined}
            renderTextLayer={false} 
            renderAnnotationLayer={false}
            loading=""
            className="react-pdf-page-wrapper"
          />
        </Document>
      )}
    </div>
  );
};

export default PdfViewer;
